import { useCallback, useMemo } from 'react';
import { DataTablePanel } from '../../../../components/data-table/DataTablePanel';
import type { DataColumn } from '../../../../components/data-table/DataTable';
import { useDataTable, type DataTableQuery } from '../../../../components/data-table/useDataTable';
import { TextCell } from '../../../../components/table/TextCell';
import { progressPercent } from '../../../../components/progressPercent';
import { formatMoney } from '../../../../lib/formatMoney';
import type { PurchaseOrderLine } from '../purchaseOrderTypes';

/**
 * The purchase order profile's Lines tab.
 *
 * ## It is a `<DataTablePanel>`, and that REVERSES the call this tab shipped with
 *
 * It was a `<PreviewGrid>`, on that shell's own test — *does this collection get WRITTEN from
 * where it is drawn?* It does not: a correction replaces the whole set through the order's own
 * form. That reading is still right about WRITING and was wrong about everything else a panel
 * brings. An order composed of several purchase requests runs to dozens of lines, and a preview
 * has no search, no sort and no pager — so the one question a reader opens this tab with
 * ("which line is the Lenovo, and which request did it come from") was a scroll.
 *
 * Product call, 05 Oct 2026, against the budget plan's own PPA roster as the reference.
 *
 * ## It is its OWN FILE because the page cannot hold a hook
 *
 * `PurchaseOrderProfilePage` guards with `if (!order) return null` and derives its live lines
 * below that, so `useCallback`/`useDataTable` there would be conditional hooks. It is also
 * CLAUDE.md's stated etiquette — a panel goes in its own file — which is what keeps a second
 * session editing this feature out of the page's own diff.
 *
 * ## Searched, sorted and paged IN MEMORY
 *
 * The 201 file's exemption, one suite along: the rule against client-side filtering governs a
 * *paged server list*, whose filter silently applies to the rows on screen and not the hundreds
 * behind them. These lines arrive WHOLE with the record, so the filter sees everything there is.
 *
 * **The sort is parsed as `field:direction`**, which is what `useDataTable.toggleSort` writes
 * and what `<DataTable>` reads back with `sort.split(':')`. Several in-memory panels elsewhere
 * parse it as a leading `-`, which never matches — so on those, descending silently does
 * nothing and any non-default column falls back to the default comparator. Do not copy that.
 *
 * ## What the reference image has and this does not
 *
 * - **No kebab gutter.** The roster's comes from `rowActions`; an order line has no row-level
 *   action, because there is no per-line endpoint — inventing one would be the dead affordance.
 * - **No banded header.** That band is the roster's own `PROPOSED BUDGET` group. These columns
 *   carry no grouping to declare, and coining one would be inventing a heading.
 * - **No `onRowOpen`.** There is nowhere to navigate: a line is not a record with a page. The
 *   anchor is a PREVIEW instead, and the two are mutually exclusive.
 *
 * ## The anchor IS a preview, and the LIST VIEW is what settles it
 *
 * It shipped with neither for an hour, on the reading that *their absence is what makes a table
 * read-only* — which is true of the table and false of the list. A list row carries the title and
 * **three** summary values, so in that view Qty, Requested, Unit cost and Line total are not on
 * screen at all: the reader can see which line it is and nothing about what it costs. That is the
 * case `<DataTablePanel>`'s own default is for — *a table row answers how this compares to the
 * others; it is a poor way to read ONE record.*
 *
 * **Looking at the list view is what found it**, and it found a second thing with it:
 * `<IndexListView>` provided `AnchorCellContext` unconditionally, so the title rendered in the
 * anchor blue at `font-medium` while opening nothing — the dead affordance, reachable only by a
 * caller supplying neither prop, which until this panel nothing did. Fixed in the shell in the
 * same change, where `<DataTable>` already had it right.
 *
 * **`size` is the default `lg`, not the order editor's `3xl`.** The rule that a preview matches
 * the record's own EDITOR has no answer here: a LINE has no editor — a correction replaces the
 * whole set through the order's form, which is a dialog about a different record two clicks away.
 * Eight short key/value rows in a `3xl` dialog would be a box mostly full of nothing.
 *
 * ## `Fulfilled` is the register's own column, one level down
 *
 * Product call, 06 Oct 2026, in the reader's own words: *in the PO Profile, line items, add column
 * Delivery Progress status*.
 *
 * **It is the WORD the register already uses, not the reader's phrase**, and that is deliberate:
 * the orders register states this same fraction under `Fulfilled` — on its table as a column and
 * on its list rows as a ring — so a second word for it one click away is the two-words-for-one-
 * concept failure inside a single module. Renaming it is a one-line change that moves BOTH.
 *
 * **ONE derivation, which is the whole reason the column is cheap.** `progressPercent` rounds
 * both surfaces, so the order-level ring and these per-line figures cannot answer one order
 * differently — and the numerator is the register's own source read one level down:
 * `GoodsReceiptItems.QuantityAccepted` on receipts at `Inspected` or `Received`, capped at the
 * line's ordered quantity. Never `PurchaseOrderItem.ReceivedQuantity`, which moves only when a
 * delivery is CLOSED and drew 0% on orders whose goods were already in stock.
 *
 * **AN EM DASH where delivery is not yet a question, and a muted `0%` where it is.** The server
 * sends `null` for the first — a draft, one awaiting a decision, one approved and still on the
 * buyer's desk — because zero there would claim nothing has arrived against an order nothing CAN
 * be delivered against. `numeric` is what dims the real zero, which is exactly the empty the eye
 * should skip past.
 *
 * **NO FOOTER.** A column of percentages does not add up — the same argument the unit cost makes
 * one column over, and the order's own fulfilment is on the header strip of this very tab.
 */
export function PurchaseOrderLinesPanel({ lines }: { lines: PurchaseOrderLine[] }) {
  const fetchLines = useCallback(
    (query: DataTableQuery) => {
      const term = (query.search ?? '').trim().toLowerCase();
      const matched = term
        ? lines.filter((line) =>
            [line.itemIdentity, line.sku, line.purchaseRequestNumber, line.unit].some((value) =>
              (value ?? '').toLowerCase().includes(term),
            ),
          )
        : lines;

      // `field:asc` / `field:desc` — the format `toggleSort` writes. Never a leading `-`.
      const [field, direction] = (query.sort ?? '').split(':');
      const descending = direction === 'desc';
      const sorted = field ? [...matched].sort(compareBy(field, descending)) : matched;

      const start = (query.page - 1) * query.pageSize;
      return Promise.resolve({
        items: sorted.slice(start, start + query.pageSize),
        total: sorted.length,
      });
    },
    [lines],
  );

  const state = useDataTable<PurchaseOrderLine>({
    fetchPage: fetchLines,
    // Generous, so the pager is a backstop rather than a wall: no realistic order reaches it,
    // and the footer below therefore totals the whole order on the one page it fits on.
    pageSize: 50,
  });

  const columns = useMemo<DataColumn<PurchaseOrderLine>[]>(
    () => [
      {
        key: 'itemIdentity',
        header: 'Item',
        width: 220,
        sortable: true,
        render: (l) => <TextCell value={l.itemIdentity} />,
      },
      {
        key: 'sku',
        header: 'SKU',
        width: 140,
        sortable: true,
        render: (l) => <TextCell value={l.sku} mono />,
      },
      {
        /*
          THE SOURCE REQUEST, per line — the column this module has and the source does not. On a
          composite order it is what tells two lines of the same product apart, which is the whole
          reason they stay two lines.

          It keeps its OWN column here, where the two grids merge it under the request line. That
          divergence is deliberate: this is a wide table that scrolls horizontally, so a column
          has a header, a width and a sort, and the room is not being competed for.
        */
        key: 'purchaseRequestNumber',
        header: 'From PR',
        width: 140,
        sortable: true,
        render: (l) => <TextCell value={l.purchaseRequestNumber} mono />,
      },
      {
        key: 'unit',
        header: 'Unit',
        width: 80,
        sortable: true,
        render: (l) => <TextCell value={l.unit} />,
      },
      {
        key: 'orderedQuantity',
        header: 'Qty',
        width: 80,
        align: 'right',
        sortable: true,
        render: (l) => <TextCell value={String(l.orderedQuantity)} mono numeric />,
        /*
          THE PAGE TOTAL, summed across the rows on screen — and that REVERSES the closed-over
          server figure this footer carried as a `<PreviewGrid>`.

          That comment read "never a sum across `rows`… the record states its own total and a
          second arithmetic here is free to disagree with it", and it rested on a premise a pager
          and a search remove: every row being on screen. With either applied, the order's own
          total would sit under a subset of its lines and claim to be what they add up to.

          Nothing is lost, which is what makes the reversal safe: the order's own quantity and
          amount are stated on the header strip — visible on this very tab — and again on the
          Info card. CLAUDE.md's standing rule is that a totals footer is the PAGE total.
        */
        footer: (rows) => (
          <span className="font-mono tabular-nums">
            {String(rows.reduce((sum, l) => sum + l.orderedQuantity, 0))}
          </span>
        ),
      },
      {
        /*
          WHAT THE REQUEST ASKED FOR, so a reader can see whether this order covers the line or
          only part of it — the source's own profile answers that only by opening the request.

          NO FOOTER: a request line may be split across two orders, so summing what was asked for
          beside what this order commits would put two figures of different kinds side by side.
        */
        key: 'requestedQuantity',
        header: 'Requested',
        width: 100,
        align: 'right',
        sortable: true,
        render: (l) => <TextCell value={String(l.requestedQuantity)} mono numeric />,
      },
      {
        /*
          HOW FAR THROUGH DELIVERY THIS LINE IS — the register's `Fulfilled` column, per line, off
          the one `progressPercent` derivation both surfaces read.

          `== null`, NEVER `=== null`: the server omits a null property entirely, so the field
          arrives `undefined` on an order that has not been dispatched and the strict test would
          answer the opposite of what it was written to say — printing `0%` on every draft line.

          SORTED ON THE PERCENTAGE rather than the quantity behind it, because that is what the
          column shows; a column ordered by a figure it does not display is a sort the reader
          cannot explain. Nulls sort LAST whichever way it points — see `compareBy`.

          NO FOOTER: percentages do not sum. The order's own fulfilment reads on the header strip
          above this tab and again on the Info card, so nothing is lost by refusing it here.
        */
        key: 'fulfilled',
        header: 'Fulfilled',
        width: 100,
        align: 'right',
        sortable: true,
        render: (l) => (
          <TextCell
            value={
              l.deliveredQuantity == null
                ? null
                : `${progressPercent(l.deliveredQuantity, l.orderedQuantity)}%`
            }
            mono
            numeric
          />
        ),
      },
      {
        /*
          NO FOOTER on a unit cost: a column of prices does not add up to anything — summing them
          produces a figure nobody was ever charged. Line total is what totals.
        */
        key: 'unitCost',
        header: 'Unit cost',
        width: 130,
        align: 'right',
        sortable: true,
        render: (l) => <TextCell value={formatMoney(l.unitCost)} mono numeric />,
      },
      {
        key: 'lineTotal',
        header: 'Line total',
        width: 140,
        align: 'right',
        sortable: true,
        render: (l) => <TextCell value={formatMoney(l.lineTotal)} mono numeric />,
        footer: (rows) => (
          <span className="font-mono tabular-nums">
            {formatMoney(rows.reduce((sum, l) => sum + l.lineTotal, 0))}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <DataTablePanel<PurchaseOrderLine>
      id="purchase-order-lines"
      state={state}
      columns={columns}
      rowKey={(l) => l.id}
      searchPlaceholder="Search item, SKU or request"
      fill
      emptyMessage="This order has no lines yet."
      emptySearchMessage="No line matches that search."
      /*
        A line carries no provenance of its own — no creator, no creation instant, no update
        trail — so the three creation columns would be em dashes and the Other Info popover would
        open on four of them. `auditColumns={false}` drops the list view's provenance lead with
        them, which is one flag for both views rather than two.
      */
      otherInfo={() => null}
      auditColumns={false}
      /* Nothing acts on a selection: a correction replaces the whole set through the order's form. */
      selectable={false}
      /*
        Built from the columns above, so the preview and the table cannot disagree about what a
        line holds, and a column added later arrives in both. `details` is deliberately absent —
        a line has no child collection.
      */
      rowPreview={{ title: 'Order line' }}
    />
  );
}

/** One comparator, so no two columns order the same kind of value differently. */
function compareBy(field: string, descending: boolean) {
  return (a: PurchaseOrderLine, b: PurchaseOrderLine) => {
    const left = sortValue(a, field);
    const right = sortValue(b, field);

    /*
      A MISSING VALUE SORTS LAST WHICHEVER WAY THE COLUMN POINTS, so the comparison returns
      before the direction is applied. A line whose order has not been dispatched has no
      fulfilment figure at all — that is the ABSENCE of an answer, not the lowest one — and
      ordering it among the zeroes would read as a line nothing has arrived against.
    */
    if (left === null || right === null) {
      if (left === right) return 0;
      return left === null ? 1 : -1;
    }

    let result: number;
    if (typeof left === 'number' && typeof right === 'number') {
      result = left - right;
    } else {
      result = String(left ?? '').localeCompare(String(right ?? ''));
    }
    return descending ? -result : result;
  };
}

function sortValue(line: PurchaseOrderLine, field: string): string | number | null {
  switch (field) {
    case 'sku':
      return line.sku;
    case 'purchaseRequestNumber':
      return line.purchaseRequestNumber;
    case 'unit':
      return line.unit;
    case 'orderedQuantity':
      return line.orderedQuantity;
    case 'requestedQuantity':
      return line.requestedQuantity;
    case 'unitCost':
      return line.unitCost;
    case 'lineTotal':
      return line.lineTotal;
    case 'fulfilled':
      // The PERCENTAGE the cell renders, never the quantity behind it.
      return line.deliveredQuantity == null
        ? null
        : progressPercent(line.deliveredQuantity, line.orderedQuantity);
    default:
      return line.itemIdentity;
  }
}
